import { useEffect, useId, useRef, useState } from 'react';
import { Check, MapPin } from 'lucide-react';
import { errorMessage, useLocale } from '../../../i18n';
import { venuePaymentSubject } from '../../../lib/api/memberPayment';
import { bookAtVenue, type VenueBooking as Booked, type VenueCourt } from '../../../lib/api/venues';
import { PayForBooking, paymentDueFor } from '../../booking/PayForBooking';
import { BigButton, Card } from '../mobileUi';
import { localDay, type VenueSlotPick } from './VenueAvailability';

// ─────────────────────────────────────────────────────────────────────
// BOOKING A PICKED TIME AT A VENUE (#833 slice 3b).
//
// The last step of Explore → Centre → Available courts → BOOKING, for somebody
// the venue's own door takes (`POST /venues/:slug/bookings`, #1361): a guest at
// a venue that has turned guests on, or anybody else the server offers it to.
//
// ── EVERY FIGURE IS THE SERVER'S ──────────────────────────────────
//
// Before booking, the price is the one the availability read sent for this
// slot at this person's tier. After booking, it is the price the door STORED —
// which is what every payment attempt charges (#1361 decision A). Nothing here
// adds, multiplies or rounds. The door decides the tier again inside its own
// transaction; the reply says which, and that is what is shown.
//
// ── PAYING IS THE MEMBER APP'S OWN «BETAL NÅ» ─────────────────────
//
// `PayForBooking`, the component the member booking and «Reservasjoner» use,
// with a venue subject: the same sheet, the same countdown from the server's
// `expiresInMs`, the same «not set up here» when the deployment has no payment
// provider. Only the route differs (`/venues/:slug/bookings/:id/payment`).
// ─────────────────────────────────────────────────────────────────────

export interface VenueBookingProps {
  slug: string;
  /** The time picked from the free list — the server's slot, with its price. */
  pick: VenueSlotPick;
  /** The venue's courts, to name them the way it does. */
  courts: VenueCourt[];
  /**
   * The answer moved under the screen (taken, no longer priced, …): the page
   * reads the day again — which clears the pick and with it this panel — so the
   * sentence saying why travels up with it, to be shown over the fresh list.
   */
  onStale: (message: string) => void;
  /** The door booked it: the page reads the day again, so the court shows as taken. */
  onBooked?: () => void;
  /** Close: back to the list, nothing picked. */
  onDone: () => void;
  /** The real one is `POST /venues/:slug/bookings`; injected in tests. */
  book?: typeof bookAtVenue;
}

/** Refusals that mean the list on screen is out of date — read it again. */
const STALE: ReadonlySet<string> = new Set([
  'slot_not_available',
  'slot_in_the_past',
  'guest_prices_not_set',
  'date_outside_booking_window',
  'court_out_of_service',
  'duration_not_allowed',
]);

type Done = { booking: Booked; priceTier: 'member' | 'guest'; expiresInMs?: number };

export function VenueBooking({
  slug,
  pick,
  courts,
  onStale,
  onBooked,
  onDone,
  book = bookAtVenue,
}: VenueBookingProps) {
  const { t, formatDate } = useLocale();
  const courtsLabel = useId();
  // One court free: it is the court. More: the person chooses.
  const [court, setCourt] = useState<number | null>(
    pick.slot.courts.length === 1 ? pick.slot.courts[0]! : null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [paid, setPaid] = useState(false);
  // INTO VIEW WHEN PICKED. On a phone the free list is long and this panel sits
  // under it: a tap on 10:00 would otherwise change nothing the person can see.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    rootRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const courtName = (n: number) =>
    courts.find((c) => c.number === n)?.name ?? t('mobile.venue.courtNumber', { number: n });
  const day = (iso: string) =>
    formatDate(localDay(iso), { weekday: 'long', day: 'numeric', month: 'long' });
  const price = (nok: number) =>
    nok === 0 ? t('mobile.activities.free') : t('mobile.venue.priceNok', { amount: String(nok) });

  const submit = async () => {
    if (court === null || busy) return;
    setBusy(true);
    setError(null);
    const outcome = await book(slug, {
      court,
      date: pick.date,
      startTime: pick.slot.startTime,
      durationMin: pick.durationMin,
    });
    setBusy(false);
    if (outcome.state === 'booked') {
      setDone({
        booking: outcome.booking,
        priceTier: outcome.priceTier,
        ...(outcome.expiresInMs === undefined ? {} : { expiresInMs: outcome.expiresInMs }),
      });
      onBooked?.();
      return;
    }
    if (outcome.state === 'unreachable') {
      // SAFE TO ASK AGAIN: the request key is the slot's, so a booking the
      // server made but could not report is answered, not repeated.
      setError(t('mobile.venue.bookUnreachable'));
      return;
    }
    const message = errorMessage(t, outcome.code);
    if (STALE.has(outcome.code)) onStale(message);
    else setError(message);
  };

  if (done) {
    const b = done.booking;
    const summary = `${courtName(b.court)} · ${day(b.date)} ${b.startTime}–${b.endTime}`;
    const owes = paymentDueFor(b) && !paid;
    return (
      <div ref={rootRef}>
        <Card>
          <div className="flex items-start gap-3" role="status">
            <div
              className="flex items-center justify-center rounded-full flex-shrink-0"
              style={{ width: 36, height: 36, background: '#c2f02f', color: '#0f1611' }}
            >
              <Check size={18} strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-semibold text-ink" style={{ fontSize: 17 }}>
                {/* NOT «the court is yours» while it still owes money (#284). */}
                {owes ? t('mobile.book.courtHeldUnpaid') : t('mobile.book.courtIsYours')}
              </div>
              <div className="text-[12px] text-ink-2 flex items-center gap-1 mt-0.5">
                <MapPin size={12} /> {summary}
              </div>
              {b.priceNok !== null && (
                <div className="text-[12px] text-ink-2 mt-0.5" data-testid="venue-booked-price">
                  {price(b.priceNok)} ·{' '}
                  {done.priceTier === 'member'
                    ? t('mobile.venue.memberPrice')
                    : t('mobile.venue.guestPrice')}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3">
            <PayForBooking
              booking={b}
              subject={venuePaymentSubject(slug)}
              summary={summary}
              // THE SERVER'S CLOCK (#607): the countdown starts from what the door
              // measured, never from a deadline and this device's own time.
              {...(done.expiresInMs === undefined ? {} : { expiresInMs: done.expiresInMs })}
              // Paying is the obvious next step, as straight after a member booking.
              autoOpen={paymentDueFor(b)}
              onPaid={() => setPaid(true)}
            />
          </div>
          <p className="text-[12px] text-ink-3 mt-3">{t('mobile.venue.bookedFindIt')}</p>
          <div className="mt-3">
            <BigButton variant="ghost" onClick={onDone}>
              {t('mobile.common.close')}
            </BigButton>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              {day(pick.date)} · {t('mobile.venue.minutes', { minutes: pick.durationMin })}
            </div>
            <div
              className="font-display font-semibold text-ink leading-tight"
              style={{ fontSize: 20 }}
            >
              {pick.slot.startTime}–{pick.slot.endTime}
            </div>
          </div>
          {pick.slot.priceNok !== null && (
            <div className="text-right flex-shrink-0">
              <div className="font-display font-semibold text-ink" style={{ fontSize: 20 }}>
                {price(pick.slot.priceNok)}
              </div>
            </div>
          )}
        </div>

        {pick.slot.courts.length > 1 ? (
          <div className="mt-3">
            <div id={courtsLabel} className="text-[12px] text-ink-2 mb-1.5">
              {t('mobile.venue.chooseCourt')}
            </div>
            <div role="group" aria-labelledby={courtsLabel} className="flex flex-wrap gap-1.5">
              {pick.slot.courts.map((n) => {
                const active = n === court;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCourt(n)}
                    aria-pressed={active}
                    className="rounded-full px-3 py-1.5 text-[12px] font-semibold"
                    style={{
                      background: active ? '#0f1611' : '#fff',
                      color: active ? '#fff' : '#0f1611',
                      border: `1px solid ${active ? '#0f1611' : '#e7eae3'}`,
                    }}
                  >
                    {courtName(n)}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-[12px] text-ink-2 flex items-center gap-1 mt-1">
            <MapPin size={12} /> {courtName(pick.slot.courts[0]!)}
          </div>
        )}

        {error && (
          <p role="alert" className="text-[13px] text-rose-700 mt-3">
            {error}
          </p>
        )}

        <div className="mt-3 space-y-2">
          <BigButton variant="lime" onClick={() => void submit()} disabled={court === null || busy}>
            {busy ? t('mobile.book.booking') : t('mobile.book.bookIt')}
          </BigButton>
          <BigButton variant="ghost" onClick={onDone} disabled={busy}>
            {t('mobile.common.cancel')}
          </BigButton>
        </div>
      </Card>
    </div>
  );
}
